import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { IFilterModel } from '@common/types';
import { IEnhancedRow } from '@components/table/types';
import ListingTableWithCellInputs from '@components/table/with-cell-inputs/ListingTableWithCellInputs';

import { getMedicalGroupInsuredDetailsHeaders } from '../constants/medicalGroupInsuredDetailsHeaders.constant';
import { FormValuesCancellation, IInsuredsDetails } from '../types';
import { getInsuredPolicyMedicalRowFieldErrors } from '../utils/getInsuredPolicyMedicalRowFieldErrors';
import { parseVarianceInputValue } from '../utils/parseVarianceInputValue';
import { recalculateInsuredPolicyMedicalVariances } from '../utils/recalculateInsuredPolicyMedicalVariances';

import { useFormikContext } from 'formik';

type VarianceField =
  | 'netPremiumVariance'
  | 'chargesVariance'
  | 'tpaFeesVariance';

type InitialVariance = Record<string, Record<VarianceField, number>>;

const VARIANCE_FIELDS: VarianceField[] = [
  'netPremiumVariance',
  'chargesVariance',
  'tpaFeesVariance'
];

const VALUES_CHANGED_MESSAGE = 'Values have been changed.';

type Props = {
  filterValues: IFilterModel;
  disabled: boolean;
  tableData: IInsuredsDetails[];
  layoutName: string;
  handlePageChange: (filter: IFilterModel) => Promise<void>;
  policyCurrency: string;
  amendmentEffectiveDate: string;
};

const MedicalGroupInsuredDetailsWidgetAtom: React.FC<Props> = ({
  filterValues,
  disabled,
  handlePageChange,
  policyCurrency,
  tableData
}) => {
  const { values, setFieldValue, errors } =
    useFormikContext<FormValuesCancellation>();
  const initialVarianceRef = useRef<InitialVariance>({});
  const baselineCapturedIdsRef = useRef('');

  const captureInitialVariances = useCallback((items: IInsuredsDetails[]) => {
    const next: InitialVariance = {};
    items.forEach((item) => {
      next[item.id] = {
        netPremiumVariance: Number(item.netPremiumVariance ?? 0),
        chargesVariance: Number(item.chargesVariance ?? 0),
        tpaFeesVariance: Number(item.tpaFeesVariance ?? 0)
      };
    });
    initialVarianceRef.current = next;
  }, []);

  useEffect(() => {
    const idsKey = tableData.map((item) => item.id).join('|');
    if (baselineCapturedIdsRef.current === idsKey) {
      return;
    }

    baselineCapturedIdsRef.current = idsKey;
    captureInitialVariances(tableData);
  }, [captureInitialVariances, tableData]);

  const displayPagedItems = useMemo(
    () =>
      tableData.map((item, index) => ({
        ...item,
        ...(values.insuredPolicyMedicals?.[index] ?? {}),
        netPremiumVarianceMinValue: -Math.abs(Number(item.netPremium ?? 0)),
        chargesVarianceMinValue: -Math.abs(Number(item.charges ?? 0)),
        tpaFeesVarianceMinValue: -Math.abs(Number(item.tpaFees ?? 0)),
        fieldValidationErrors: getInsuredPolicyMedicalRowFieldErrors(
          errors,
          index
        )
      })),
    [errors, tableData, values.insuredPolicyMedicals]
  );

  const dynamicRowMessage = (row: IEnhancedRow): string[] => {
    const initial = initialVarianceRef.current[row.columns.id];
    if (!initial) {
      return [];
    }

    for (const field of VARIANCE_FIELDS) {
      if (Number(row.columns[field]) !== Number(initial[field])) {
        return [VALUES_CHANGED_MESSAGE];
      }
    }

    return [];
  };

  const handleTableValueChange = (
    row: IInsuredsDetails,
    columnName: string,
    value: unknown,
    _rowId: string,
    index: number
  ) => {
    const parsedValue = parseVarianceInputValue(value);
    if (parsedValue === null) {
      return;
    }

    setFieldValue(
      `insuredPolicyMedicals[${index}].${columnName}`,
      parsedValue,
      true
    );
  };

  const validateTableAmountReceived = (
    index: number,
    columnName: string,
    value: unknown,
    _row: IInsuredsDetails
  ) => {
    const parsedValue = parseVarianceInputValue(value, { finalize: true });
    if (parsedValue === null) {
      return;
    }

    const currentRow: IInsuredsDetails = {
      ...values.insuredPolicyMedicals[index],
      [columnName]: parsedValue
    };

    const recalculated = recalculateInsuredPolicyMedicalVariances(currentRow);

    setFieldValue(
      `insuredPolicyMedicals[${index}].grossPremiumVariance`,
      recalculated.grossPremiumVariance,
      true
    );
    setFieldValue(
      `insuredPolicyMedicals[${index}].coverProportionalStamp`,
      recalculated.coverProportionalStamp,
      true
    );
    setFieldValue(
      `insuredPolicyMedicals[${index}].coverMunicipalityTax`,
      recalculated.coverMunicipalityTax,
      true
    );
    setFieldValue(
      `insuredPolicyMedicals[${index}].totalPremiumVariance`,
      recalculated.totalPremiumVariance,
      true
    );
  };

  return (
    <ListingTableWithCellInputs
      name="medicalGroupInsuredDetails"
      inlineTitle="Insured Details"
      headers={getMedicalGroupInsuredDetailsHeaders(policyCurrency, disabled)}
      data={{
        pagedItems: displayPagedItems,
        pageNumber: filterValues.pagination?.pageNumber || 1,
        pageSize: filterValues.pagination?.pageSize || 5,
        totalCount: values.totalCount ?? 0
      }}
      handleCellValueChanged={(index, columnName, value, rowId, row) => {
        handleTableValueChange(
          row as IInsuredsDetails,
          columnName,
          value,
          rowId,
          index
        );
      }}
      handleUpdate={(index, columnName, value, row) => {
        validateTableAmountReceived(
          index,
          columnName,
          value,
          row as IInsuredsDetails
        );
      }}
      inline
      scrollable
      dynamicRowMessage={dynamicRowMessage}
      disableSelection
      defaultOrderByColumn="policy.policyNumber"
      orderByAscendingByDefault
      rowsPerPageOptions={[5, 10, 25]}
      onPageChange={(page, pageSize) => {
        const newFilterModel: IFilterModel = {
          ...filterValues,
          pagination: {
            pageNumber: page,
            pageSize: pageSize
          },
          orderBy: filterValues.orderBy,
          descending: filterValues.descending,
          searchKey: filterValues.searchKey
        };

        return handlePageChange(newFilterModel);
      }}
      onSortChange={(order, orderBy) => {
        const incomingOrderBy = order?.trim();

        const newFilterModel: IFilterModel = {
          ...filterValues,
          pagination: {
            pageNumber: filterValues.pagination.pageNumber,
            pageSize: filterValues.pagination.pageSize
          },
          orderBy: incomingOrderBy || filterValues.orderBy,
          descending: orderBy,
          searchKey: filterValues.searchKey
        };

        return handlePageChange(newFilterModel);
      }}
    />
  );
};

export { MedicalGroupInsuredDetailsWidgetAtom };
